import { Icon } from "../../components/Icon";
import { Badge, Card, Meter, PageHead, Skeleton } from "../../components/kit";
import { useStats } from "../../lib/hooks";

export function AchievementsPage() {
  const stats = useStats();

  return (
    <div className="page">
      <PageHead title="Achievements" sub="Badges, streaks and certificates you’ve earned along the way." />

      {!stats ? (
        <AchievementsSkeleton />
      ) : (
        <Loaded stats={stats} />
      )}
    </div>
  );
}

function Loaded({ stats }: { stats: NonNullable<ReturnType<typeof useStats>> }) {
  const achievements = stats.achievements;
  const earned = achievements.filter((a) => a.done).length;
  const mastered = stats.mastered;
  const total = stats.totalLessons;
  const certPct = total > 0 ? Math.round((mastered / total) * 100) : 0;
  const remaining = Math.max(0, total - mastered);

  return (
    <>
      <div className="ach-banner">
        <div className="ach-banner-l">
          <div className="eyebrow-sm">{remaining === 0 ? "Certificate earned" : "Certificate in progress"}</div>
          <h3>Curriculum completion</h3>
          <p>{remaining === 0 ? "You've completed every available lesson." : `Complete ${remaining} more lesson${remaining === 1 ? "" : "s"} to finish your curriculum.`}</p>
          <div className="ach-prog"><Meter pct={certPct} /><span className="muted-sm">{mastered} / {total} lessons</span></div>
        </div>
        <span className="ach-cert"><Icon name="badge-check" size={40} strokeWidth={1.5} /></span>
      </div>

      <Card title="Badges" icon="award" count={`${earned} of ${achievements.length}`} pad={false}>
        <div className="badge-grid">
          {achievements.map((a) => (
            <div className={`ach ${a.done ? "done" : "locked"}`} key={a.id}>
              <span className="ach-ic"><Icon name={a.ic} size={22} strokeWidth={1.7} /></span>
              <div className="ach-t">{a.t}</div>
              {a.done ? (
                <Badge tone="live" icon="check">Earned</Badge>
              ) : (
                <div className="ach-bp">
                  <Meter pct={Math.min(100, ((a.prog ?? 0) / (a.goal ?? 1)) * 100)} height={5} />
                  <span className="muted-sm">{a.prog}/{a.goal}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

function AchievementsSkeleton() {
  return (
    <>
      <div className="ach-banner">
        <div className="ach-banner-l" style={{ flex: 1 }}>
          <Skeleton w={120} h={11} style={{ marginBottom: 12 }} />
          <Skeleton w={220} h={22} style={{ marginBottom: 12 }} />
          <Skeleton w="80%" h={13} style={{ marginBottom: 16 }} />
          <Skeleton w={240} h={9} r={5} />
        </div>
        <Skeleton w={78} h={78} r={40} />
      </div>

      <Card title="Badges" icon="award" pad={false}>
        <div className="badge-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div className="ach locked" key={i}>
              <Skeleton w={44} h={44} r={12} style={{ margin: "0 auto 12px" }} />
              <Skeleton w="70%" h={13} style={{ margin: "0 auto 10px" }} />
              <Skeleton w="50%" h={18} r={9} style={{ margin: "0 auto" }} />
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}
